import { LoadingContainer } from 'peach/components'
import { useHistory, useParams } from 'peach/router'
import * as R from 'remeda'

import { DynamicForm, Page, PageHeader } from 'core/components'
import { useInternalSupercaseCreate } from 'core/types'
import { parseDatetime } from 'core/helpers'

const CreateSupercase = () => {
  const history = useHistory()
  const { companyId } = useParams<{ companyId?: string }>()

  const { mutateAsync: createSupercase } = useInternalSupercaseCreate({
    companyId,
  })

  return (
    <LoadingContainer>
      <Page variant='full'>
        <PageHeader title='Create Supercase' />
        <DynamicForm
          data={R.pipe(
            {
              supercaseType: '',
              name: '',
              description: '',
              eventStartedAt: '',
              externalId: '',
              status: '',
              outcome: '',
              subcaseTypeId: '',
              subcaseName: '',
              autoCreated: false,
              femaDisasterNumber: 0,
              femaMonitoringEndDate: '',
            },
            // Convert any null values to empty strings for string fields to prevent React warnings
            R.mapValues((v) => (v === null ? '' : v)),
          )}
          fieldEditMode='all'
          fieldOverrides={{
            supercaseType: 'Select',
            status: 'Select',
            outcome: 'Select',
          }}
          ignoreIsDirty
          omitFields={[
            'id',
            'createdAt',
            'deletedAt',
            'updatedAt',
            'object',
            'companyId',
            'statusUpdatedAt',
            'createdBy',
          ]}
          onSubmit={async (payload: any) => {
            const processedPayload = R.pipe(
              payload,
              // Convert eventStartedAt from datetime-local to ISO 8601
              (p) => {
                if (p.eventStartedAt && typeof p.eventStartedAt === 'string') {
                  const dateTimeValue = p.eventStartedAt.trim()
                  if (dateTimeValue) {
                    const date = parseDatetime(dateTimeValue)
                    if (!Number.isNaN(date.getTime())) {
                      return { ...p, eventStartedAt: date.toISOString() }
                    }
                  }
                }
                return p
              },
              // Convert femaMonitoringEndDate - keep as is if valid date string
              (p) => {
                if (
                  p.femaMonitoringEndDate &&
                  typeof p.femaMonitoringEndDate === 'string'
                ) {
                  const dateValue = p.femaMonitoringEndDate.trim()
                  return dateValue
                    ? { ...p, femaMonitoringEndDate: dateValue }
                    : p
                }
                return p
              },
              // Remove empty strings, null, undefined, and zero numbers
              R.omitBy((v) => {
                if (v === '' || v === null || v === undefined) return true
                if (typeof v === 'number' && v === 0) return true
                return false
              }),
            )

            const res = await createSupercase(processedPayload)
            const id = (res as any)?.data?.id as string | undefined
            if (id) {
              history.push(`/companies/${companyId}/supercases/${id}`)
            } else {
              history.push(`/companies/${companyId}/supercases`)
            }
          }}
          selectOptions={{
            supercaseType: [
              { label: 'Generic', value: 'generic' },
              { label: 'Federal Emergency', value: 'federalEmergency' },
            ],
            status: [
              { label: 'Initiated', value: 'initiated' },
              { label: 'Processing', value: 'processing' },
              { label: 'Completed', value: 'completed' },
              { label: 'Canceled', value: 'canceled' },
              { label: 'Reopened', value: 'reopened' },
            ],
            outcome: [
              { label: 'Approved', value: 'approved' },
              { label: 'Denied', value: 'denied' },
              { label: 'False Positive', value: 'falsePositive' },
              { label: 'No Outcome', value: 'noOutcome' },
            ],
          }}
          submitText='Create Supercase'
          validationRules={{
            supercaseType: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'supercaseType is required',
              (v: unknown) =>
                v === 'generic' ||
                v === 'federalEmergency' ||
                'supercaseType must be "generic" or "federalEmergency"',
            ],
            name: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'name is required',
              (v: unknown) => {
                if (typeof v !== 'string') return true
                return v.length <= 128 || 'name must be 128 characters or less'
              },
            ],
            externalId: [
              (v: unknown) => {
                if (!v || typeof v !== 'string' || v.trim().length === 0)
                  return true
                return (
                  (v.length >= 1 && v.length <= 50) ||
                  'externalId must be between 1 and 50 characters'
                )
              },
            ],
            status: [
              (v: unknown) => {
                if (!v || typeof v !== 'string' || v.trim().length === 0)
                  return true
                return (
                  [
                    'initiated',
                    'processing',
                    'completed',
                    'canceled',
                    'reopened',
                  ].includes(v as string) ||
                  'status must be one of "initiated", "processing", "completed", "canceled", "reopened"'
                )
              },
            ],
            outcome: [
              (v: unknown) => {
                if (!v || typeof v !== 'string' || v.trim().length === 0)
                  return true
                return (
                  ['approved', 'denied', 'falsePositive', 'noOutcome'].includes(
                    v as string,
                  ) ||
                  'outcome must be one of "approved", "denied", "falsePositive", "noOutcome"'
                )
              },
            ],
            femaDisasterNumber: [
              (v: unknown) => {
                if (v === null || v === undefined || v === 0) return true
                return (
                  (typeof v === 'number' && v >= 0) ||
                  'femaDisasterNumber must be a non-negative number'
                )
              },
            ],
          }}
        />
      </Page>
    </LoadingContainer>
  )
}

export default CreateSupercase
